import { httpDownload, httpGet, httpGetResponse, httpPost, httpPostForm } from '../../api/httpClient';
import {
  AdminListResult,
  AdminPriceImport,
  AdminPriceImportCreateRequest,
  AdminPriceImportCreateProductAndMatchRequest,
  AdminPriceImportListParams,
  AdminPriceImportMatchBatchRequest,
  AdminPriceImportMatchRequest,
  AdminPriceImportRejectRequest,
  AdminPriceImportRow,
  AdminPriceImportRowsListParams,
  AdminPriceImportStatusUpdateRequest,
  AdminStoredFile,
} from '../../api/types';

export const priceImportApi = {
  uploadImportFile(file: File): Promise<AdminStoredFile> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_public_name', file.name);

    return httpPostForm<AdminStoredFile>('/v1/price-import/file', formData);
  },

  createImport(payload: AdminPriceImportCreateRequest): Promise<AdminPriceImport> {
    return httpPost<AdminPriceImport, AdminPriceImportCreateRequest>('/v1/price-import', payload);
  },

  async getImports(params: AdminPriceImportListParams): Promise<AdminListResult<AdminPriceImport>> {
    const response = await httpGetResponse<AdminPriceImport[]>('/v1/price-import', createImportQueryParams(params));

    return {
      items: response.data,
      meta: response.meta,
    };
  },

  getImport(importId: number): Promise<AdminPriceImport> {
    return httpGet<AdminPriceImport>(`/v1/price-import/${importId}`, {});
  },

  async getImportRows(importId: number, params: AdminPriceImportRowsListParams): Promise<AdminListResult<AdminPriceImportRow>> {
    const response = await httpGetResponse<AdminPriceImportRow[]>(`/v1/price-import/${importId}/rows`, createImportRowsQueryParams(params));

    return {
      items: response.data,
      meta: response.meta,
    };
  },

  async getAllImportRows(
    importId: number,
    params: AdminPriceImportRowsListParams,
  ): Promise<AdminListResult<AdminPriceImportRow>> {
    const firstPage = await priceImportApi.getImportRows(importId, {
      ...params,
      page: 1,
      per_page: 500,
    });
    const pageCount = Number(firstPage.meta.page_count ?? 1);

    if (pageCount <= 1) {
      return firstPage;
    }

    const remainingPageNumbers = Array.from(
      { length: pageCount - 1 },
      (_value, index) => index + 2,
    );
    const remainingPages = await Promise.all(
      remainingPageNumbers.map((page) => priceImportApi.getImportRows(importId, {
        ...params,
        page,
        per_page: 500,
      })),
    );
    const remainingItems = remainingPages.flatMap((page) => page.items);

    return {
      items: [...firstPage.items, ...remainingItems],
      meta: firstPage.meta,
    };
  },

  matchRow(importId: number, rowId: number, payload: AdminPriceImportMatchRequest): Promise<AdminPriceImport> {
    return httpPost<AdminPriceImport, AdminPriceImportMatchRequest>(
      `/v1/price-import/${importId}/rows/${rowId}/match`,
      payload,
    );
  },

  saveMatches(importId: number, payload: AdminPriceImportMatchBatchRequest): Promise<AdminPriceImport> {
    return httpPost<AdminPriceImport, AdminPriceImportMatchBatchRequest>(
      `/v1/price-import/${importId}/matches`,
      payload,
    );
  },

  createProductAndMatch(
    importId: number,
    rowId: number,
    payload: AdminPriceImportCreateProductAndMatchRequest,
  ): Promise<AdminPriceImport> {
    return httpPost<AdminPriceImport, AdminPriceImportCreateProductAndMatchRequest>(
      `/v1/price-import/${importId}/rows/${rowId}/create-product-and-match`,
      payload,
    );
  },

  rejectImport(importId: number, payload: AdminPriceImportRejectRequest): Promise<AdminPriceImport> {
    return httpPost<AdminPriceImport, AdminPriceImportRejectRequest>(`/v1/price-import/${importId}/reject`, payload);
  },

  updateImportStatus(importId: number, payload: AdminPriceImportStatusUpdateRequest): Promise<AdminPriceImport> {
    return httpPost<AdminPriceImport, AdminPriceImportStatusUpdateRequest>(`/v1/price-import/${importId}/status`, payload);
  },

  processImport(importId: number): Promise<AdminPriceImport> {
    return httpPost<AdminPriceImport, Record<string, never>>(`/v1/price-import/${importId}/recognize`, {});
  },

  applyImportPrices(importId: number): Promise<AdminPriceImport> {
    return httpPost<AdminPriceImport, Record<string, never>>(`/v1/price-import/${importId}/apply-prices`, {});
  },

  downloadImport(importId: number, fileName?: string): Promise<void> {
    return httpDownload(`/v1/price-import/${importId}/download`, undefined, fileName);
  },
};

function createImportQueryParams(params: AdminPriceImportListParams): Record<string, string> {
  const queryParams: Record<string, string> = {};

  if (params.company_id !== undefined && params.company_id !== null) {
    queryParams.company_id = String(params.company_id);
  }

  if (params.company_name !== undefined && params.company_name !== '') {
    queryParams.company_name = params.company_name;
  }

  if (params.status_id !== undefined && params.status_id !== null) {
    queryParams.status_id = String(params.status_id);
  }

  queryParams.page = String(params.page ?? 1);
  queryParams.per_page = String(params.per_page ?? 100);

  return queryParams;
}

function createImportRowsQueryParams(params: AdminPriceImportRowsListParams): Record<string, string> {
  const queryParams: Record<string, string> = {};

  if (params.status_id !== undefined && params.status_id !== null) {
    queryParams.status_id = String(params.status_id);
  }

  if (params.only_unmatched) {
    queryParams.only_unmatched = '1';
  }

  if (params.match_state !== undefined) {
    queryParams.match_state = params.match_state;
  }

  queryParams.page = String(params.page ?? 1);
  queryParams.per_page = String(params.per_page ?? 500);

  return queryParams;
}
